import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Navigation, MapPin, Radio, Loader2 } from 'lucide-react';
import { apiWorkorders, apiCustomers, team, geo } from '../../lib/api';
import { statusColor, useJobStatuses } from '../../lib/jobStatus';
import { today, normDate } from '../../lib/dates';

const statusHex = (s) => statusColor(s);
// Leaflet popups and markers take raw HTML; names and addresses come from
// customers and imports, so they are escaped before going in.
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// Older pings were stored without a UTC marker; read those as UTC too.
const pingTime = (ts) => (ts ? new Date(/[zZ]|[+-]\d\d:\d\d$/.test(ts) ? ts : `${ts}Z`) : null);
const STALE_MIN = 15;
const minutesAgo = (ts) => { const d = pingTime(ts); return d ? Math.max(0, Math.round((Date.now() - d.getTime()) / 60000)) : null; };
const seenLabel = (ts) => { const m = minutesAgo(ts); if (m == null) return '—'; if (m < 1) return 'just now'; if (m < 60) return `${m} min ago`; const d = pingTime(ts); return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`; };
const gmapsAt = (lat, lng) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
const HQ = [33.8314, -118.2820]; // Carson, CA

const pinIcon = (color, label) => L.divIcon({
  className: '', iconSize: [28, 36], iconAnchor: [14, 36],
  html: `<svg width="28" height="36" viewBox="0 0 24 32"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="${color}"/><circle cx="12" cy="12" r="5" fill="white"/><text x="12" y="15.5" font-size="8" font-weight="bold" text-anchor="middle" fill="${color}">${label || ''}</text></svg>`,
});
const techIcon = (initials) => L.divIcon({
  className: '', iconSize: [38, 38], iconAnchor: [19, 19],
  html: `<div style="position:relative;width:38px;height:38px"><span style="position:absolute;inset:0;border-radius:50%;background:#0ea5e9;opacity:.25;animation:xpping 1.4s infinite"></span><div style="position:relative;width:38px;height:38px;border-radius:50%;background:#0ea5e9;color:#fff;font-weight:bold;font-size:11px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${initials}</div></div>`,
});

export default function MapPage() {
  useJobStatuses();   // populates the colour cache statusHex reads
  const [date, setDate] = useState(today());
  const [jobs, setJobs] = useState([]);
  const [coords, setCoords] = useState({});       // address -> {lat,lng}
  const [locations, setLocations] = useState([]); // live tech locations
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const jobLayer = useRef(null);
  const techLayer = useRef(null);

  // init map once
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { scrollWheelZoom: true }).setView(HQ, 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
    }).addTo(map);
    jobLayer.current = L.layerGroup().addTo(map);
    techLayer.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
  }, []);

  const fullAddr = useCallback((j, custMap) => {
    const c = custMap[j.customerId];
    if (c) return [j.address || c.address, c.city, c.state, c.zip].filter(Boolean).join(', ');
    return j.address || '';
  }, []);

  const load = useCallback(async () => {
    try {
      const [wo, cust] = await Promise.all([apiWorkorders.list().catch(() => []), apiCustomers.list().catch(() => [])]);
      const custMap = Object.fromEntries((cust || []).map(c => [c.id, c]));
      const dayJobs = (wo || []).filter(j => normDate(j.date) === date && j.status !== 'Cancelled')
        .map(j => ({ ...j, _addr: fullAddr(j, custMap) }));
      setJobs(dayJobs);
      // geocode the day's unique addresses
      const addrs = [...new Set(dayJobs.map(j => j._addr).filter(Boolean))];
      const missing = addrs.filter(a => !(a in coords));
      if (missing.length) {
        setGeocoding(true);
        const res = await geo.batch(missing).catch(() => ({}));
        setCoords(prev => ({ ...prev, ...res }));
        setGeocoding(false);
      }
    } finally { setLoading(false); }
  }, [date, coords, fullAddr]);

  useEffect(() => { load(); }, [date]); // eslint-disable-line

  // poll live tech locations
  useEffect(() => {
    const poll = () => team.locations().then(setLocations).catch(() => {});
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, []);

  // render job markers
  useEffect(() => {
    if (!jobLayer.current) return;
    jobLayer.current.clearLayers();
    const pts = [];
    jobs.forEach(j => {
      const c = coords[j._addr];
      if (!c || c.lat == null) return;
      pts.push([c.lat, c.lng]);
      L.marker([c.lat, c.lng], { icon: pinIcon(statusHex(j.status)) })
        .bindPopup(`<b>${esc(j.customer)}</b><br>${esc(j.service)}<br><span style="color:#64748b">${esc(j.time)} · ${esc(j.tech || 'Unassigned')}</span><br><span style="color:${esc(statusHex(j.status))};font-weight:bold">${esc(j.status)}</span>`)
        .addTo(jobLayer.current);
    });
    if (pts.length && mapRef.current) {
      try { mapRef.current.fitBounds(pts, { padding: [50, 50], maxZoom: 14 }); } catch { /* noop */ }
    }
  }, [jobs, coords]);

  // render live tech markers
  useEffect(() => {
    if (!techLayer.current) return;
    techLayer.current.clearLayers();
    locations.forEach(t => {
      if (t.lat == null) return;
      const initials = (t.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('');
      L.marker([t.lat, t.lng], { icon: techIcon(esc(initials)), zIndexOffset: 1000, opacity: minutesAgo(t.ts) > STALE_MIN ? 0.5 : 1 })
        .bindPopup(`<b>${esc(t.name)}</b><br><span style="color:#64748b">Seen ${esc(seenLabel(t.ts))}</span><br><a href="${gmapsAt(Number(t.lat), Number(t.lng))}" target="_blank" rel="noreferrer">Open in Google Maps</a>`)
        .addTo(techLayer.current);
    });
  }, [locations]);

  const counts = ['Completed', 'In Progress', 'En Route', 'Scheduled'].map(s => ({ s, n: jobs.filter(j => j.status === s).length }));

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <style>{`@keyframes xpping{75%,100%{transform:scale(2);opacity:0}}`}</style>
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">Live GPS Map {geocoding && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}<Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1"><Radio className="w-3 h-3 animate-pulse" />LIVE</Badge></h1>
          <p className="text-slate-500 mt-1">Real map · {jobs.length} stops · {locations.length} technician(s) reporting GPS</p>
        </div>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-10 w-44" data-testid="map-date-input" />
      </div>
      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <Card className="p-0 border-slate-200 overflow-hidden">
          <div ref={mapEl} className="h-[640px] w-full bg-slate-100" data-testid="leaflet-map" />
        </Card>
        <div className="space-y-3">
          <Card className="p-4 border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3">Technicians On Duty</h3>
            {locations.length === 0 ? <div className="text-sm text-slate-400">No live GPS yet. Technicians' phones report their location while the XP-PRO tech app is open on screen.</div> : (
              <div className="space-y-3">
                {locations.map(t => (
                  <div key={t.email} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0ea5e9] text-white text-sm font-bold flex items-center justify-center">{(t.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
                    <div className="flex-1"><div className="font-semibold text-slate-900 text-sm">{t.name}</div><div className="text-xs text-slate-500">Seen {seenLabel(t.ts)}{t.lat != null && <> · <a href={gmapsAt(t.lat, t.lng)} target="_blank" rel="noreferrer" className="text-[#0ea5e9] font-semibold">Google Maps</a></>}</div></div>
                    <span className={`w-2 h-2 rounded-full ${minutesAgo(t.ts) > STALE_MIN ? 'bg-slate-300' : 'bg-emerald-500 animate-pulse'}`} title={minutesAgo(t.ts) > STALE_MIN ? 'No update recently' : 'Live'} />
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card className="p-4 border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><Navigation className="w-4 h-4 text-[#8BC53F]" />Job Status</h3>
            <div className="space-y-2 text-sm">
              {counts.map(({ s, n }) => (
                <div key={s} className="flex justify-between items-center">
                  <span className="flex items-center gap-2 text-slate-600"><MapPin className="w-3.5 h-3.5" style={{ color: statusHex(s) }} fill={statusHex(s)} />{s}</span>
                  <Badge className="bg-slate-100 text-slate-700 border-0">{n}</Badge>
                </div>
              ))}
            </div>
          </Card>
          {loading && <div className="text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>}
        </div>
      </div>
    </div>
  );
}
